import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Search, Users } from 'lucide-react';
import clsx from 'clsx';
import type { SavedQuery } from '@idas/shared';
import { api, ApiError } from '../lib/api';
import { Modal } from './ui/Modal';
import { ListSkeleton } from './ui/EmptyState';
import { toast } from './ui/Toast';

/**
 * Handing a saved query to named people.
 *
 * The existing "shared" switch is all-or-nothing — everyone in the console
 * sees it, or nobody does. This is the middle ground: an administrator writes
 * a query once and gives it to the handful of people whose job it is, without
 * publishing it to everyone else.
 *
 * Sharing conveys *visibility*, never authority. The recipient runs it as
 * themselves, so their own connection grants, masking and limits apply — a
 * query shared with someone who cannot reach the table still refuses for them.
 * That is what makes this safe to put in front of an administrator: it cannot
 * be used to widen anyone's access, only to save them writing the SQL.
 */
export function ShareQueryModal({
  query,
  onClose,
}: {
  query: SavedQuery;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>(query.sharedWith);

  // Only active people. Sharing with a suspended account would quietly
  // reappear when it is reinstated, long after anyone remembers deciding it.
  const people = useQuery({
    queryKey: ['users', 'ACTIVE', ''],
    queryFn: () => api.users.list({ status: 'ACTIVE' }),
  });

  const visible = useMemo(() => {
    const all = (people.data?.users ?? []).filter((u) => u.id !== query.ownerId);
    const needle = search.trim().toLowerCase();
    if (!needle) return all;
    return all.filter(
      (u) =>
        u.name.toLowerCase().includes(needle) ||
        u.email.toLowerCase().includes(needle) ||
        (u.employee?.department ?? '').toLowerCase().includes(needle),
    );
  }, [people.data, search, query.ownerId]);

  const save = useMutation({
    mutationFn: () => api.savedQueries.setShares(query.id, { userIds: selected }),
    onSuccess: (updated) => {
      const n = updated.sharedWith.length;
      toast.success(
        n === 0
          ? `“${query.name}” is no longer shared with anyone.`
          : `“${query.name}” is now visible to ${n} ${n === 1 ? 'person' : 'people'}.`,
      );
      void queryClient.invalidateQueries({ queryKey: ['saved-queries'] });
      onClose();
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not share that query.'),
  });

  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const changed =
    selected.length !== query.sharedWith.length ||
    selected.some((id) => !query.sharedWith.includes(id));

  return (
    <Modal
      title={`Share “${query.name}”`}
      description="The people chosen here see this query in their console and dashboards."
      onClose={onClose}
      footer={
        <>
          <span className="mr-auto text-2xs text-ink-muted">
            {selected.length === 0
              ? 'Nobody selected'
              : `${selected.length} selected`}
          </span>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!changed || save.isPending}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {query.shared && (
          <p className="rounded-md border border-warn/30 bg-warn/5 p-2.5 text-2xs leading-relaxed text-warn">
            This query is also switched to <b>shared with everyone</b>, so it is visible to the
            whole console regardless of what you choose here. Turn that off in Edit if you want it
            limited to these people.
          </p>
        )}

        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-ink-faint" />
          <input
            className="input py-1 pl-7"
            placeholder="Search by name, email or department"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {people.isLoading && <ListSkeleton rows={5} height={38} />}

        {!people.isLoading && visible.length === 0 && (
          <p className="py-6 text-center text-2xs text-ink-muted">
            {search ? 'Nobody matches that.' : 'There is nobody else with console access yet.'}
          </p>
        )}

        <div className="max-h-72 space-y-1 overflow-y-auto">
          {visible.map((person) => {
            const on = selected.includes(person.id);
            return (
              <button
                key={person.id}
                className={clsx(
                  'flex w-full items-center gap-2 rounded-md border p-2 text-left transition-colors',
                  on ? 'border-accent bg-accent/5' : 'border-edge hover:bg-surface-sunken',
                )}
                onClick={() => toggle(person.id)}
              >
                <span
                  className={clsx(
                    'flex h-3 w-3 shrink-0 items-center justify-center rounded-[3px] border',
                    on ? 'border-accent bg-accent text-white' : 'border-edge-strong',
                  )}
                >
                  {on && <Check className="h-2.5 w-2.5" strokeWidth={3} />}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-2xs font-medium text-ink">
                    {person.name}
                  </span>
                  <span className="block truncate text-2xs text-ink-muted">
                    {person.email}
                    {/* Their real job, so an administrator sharing a finance
                        query can tell two similar names apart. */}
                    {person.employee?.designation ? ` · ${person.employee.designation}` : ''}
                  </span>
                </span>

                <span className="chip shrink-0 border-edge bg-surface-sunken text-ink-faint">
                  {person.role.replace('_', ' ').toLowerCase()}
                </span>
              </button>
            );
          })}
        </div>

        <p className="text-2xs leading-relaxed text-ink-faint">
          <Users className="mr-1 inline h-2.5 w-2.5" />
          Sharing only makes the query visible. Everyone runs it as themselves, so their own
          database access and masking still apply.
        </p>
      </div>
    </Modal>
  );
}
